import { Button, Card, CardActions, CardContent, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { ErrorMessage } from 'components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Slot } from 'services';
import { ModelsActions } from "store/actions";
import useErrorCatcher from "utils/useErrorCatcher";
import validate from "validate.js";
import ErrorIcon from '@material-ui/icons/Error';
import moment from "moment";

validate.validators.slots = function (value, options, key, attributes) {
  let { start_at, end_at } = attributes;
  if (start_at && end_at && moment(end_at).diff(moment(start_at), "s") < 0) {
    return "^End timing cannot come before start timing";
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  helper: {
    color: colors.orange[900],
  }
}));

const schema = {
  start_at: {
    presence: { allowEmpty: false, message: "is required" },
    slots: true,
  },
  end_at: {
    presence: { allowEmpty: false, message: "is required" },
    slots: true,
  }
};

const SlotEdit = props => {
  const { open, onClose, className, ...rest } = props;

  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && error.message));
  const model = useSelector(state => state.models.slot);
  const dispatch = useDispatch();
  const [formState, setFormState] = useState({
    values: {
      start_at: moment(model.start_at).format("YYYY-MM-DDTHH:mm"),
      end_at: moment(model.start_at).add(model.duration, "seconds").format("YYYY-MM-DDTHH:mm"),
      limit: model.limit || 0
    },
    touched: {},
    errors: {}
  });
  const diff = moment(formState.values.end_at).diff(moment(formState.values.start_at), "m");
  useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
  }, [formState.values]);

  if (!open) {
    return null;
  }

  const handleClose = event => {
    event.preventDefault();

    setFormState(formState => ({
      ...formState,
      touched: {
        // to show error message
        start_at: true,
        end_at: true,
      }
    }));

    if (Object.keys(formState.errors).length) return;

    setLoading(true);
    runAsyncBlock(async () => {
      const response = await Slot.update(model.id,
        {
          start_at: moment(formState.values.start_at).format("YYYY-MM-DD HH:mm:ss"),
          duration: moment(formState.values.end_at).diff(moment(formState.values.start_at), "seconds"),
          limit: formState.values.limit
        });
      dispatch(ModelsActions.update_model({ slot: response.model }));
    }).finally(() => {
      setLoading(false);
      onClose();
    });

  }

  const handleChange = event => {
    event.persist();
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true
      }
    }));
  };

  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <Modal
      onClose={onClose}
      open={open}>
      <Card
        {...rest}
        className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Edit Slot
            </Typography>
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={6} xs={6}>
                <TextField
                  fullWidth
                  label="Start At"
                  name="start_at"
                  onChange={handleChange}
                  type="datetime-local"
                  variant="outlined"
                  value={formState.values.start_at}
                  error={hasError("start_at")}
                  helperText={hasError("start_at") ? formState.errors["start_at"][0] : null}
                />
              </Grid>
              <Grid item md={6} xs={6}>
                <TextField
                  fullWidth
                  label="End At"
                  name="end_at"
                  onChange={handleChange}
                  type="datetime-local"
                  variant="outlined"
                  value={formState.values.end_at}
                  error={hasError("end_at")}
                  helperText={hasError("end_at") ? formState.errors["end_at"][0] : null}
                />
              </Grid>
              <Grid item md={6} xs={6}>
                <TextField
                  fullWidth
                  label="Duration (min)"
                  name="duration"
                  onChange={handleChange}
                  type="number"
                  variant="outlined"
                  helperText={diff > 720 ? <Typography variant="caption" className={classes.helper}><ErrorIcon fontSize="inherit" />{` Please confirm that the lesson is ${diff} minutes long`}</Typography> : null}
                  value={diff}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid item md={6} xs={6}>
                <TextField
                  fullWidth
                  label="Limit"
                  name="limit"
                  onChange={handleChange}
                  type="number"
                  variant="outlined"
                  helperText={<Typography variant="caption">Set 0 for no limit</Typography>}
                  value={formState.values.limit}
                />
              </Grid>
              <ErrorMessage message={errorMessage} className={classes.error} />
              <Grid item />
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleClose} variant="contained" disabled={loading} >Save</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
};

SlotEdit.propTypes = {
  className: PropTypes.string,
  model: PropTypes.any,
  setModel: PropTypes.func,
  onClose: PropTypes.func,
  open: PropTypes.bool
};

SlotEdit.defaultProps = {
  open: false,
  onClose: () => { }
};

export default SlotEdit;
